import React, { useState, useEffect } from 'react';
import { API } from '../App';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { CODE_ORDER } from '../codes';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f1a',
    display: 'flex',
  },
  main: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  pageTitle: {
    color: '#ffffff',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  pageSubtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '40px',
  },
  card: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  select: {
    padding: '10px 14px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
  },
  input: {
    padding: '10px 14px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
  },
  generateButton: {
    padding: '14px 32px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  disabledButton: {
    padding: '14px 32px',
    backgroundColor: '#444',
    border: 'none',
    borderRadius: '10px',
    color: '#888',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'not-allowed',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#0f3460',
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: '16px',
  },
  progressFill: {
    height: '8px',
    backgroundColor: '#e94560',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  progressText: {
    color: '#a0a0b0',
    fontSize: '13px',
    marginTop: '8px',
    textAlign: 'center',
  },
  previewTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #0f3460',
  },
  td: {
    color: '#ffffff',
    fontSize: '14px',
    padding: '12px 16px',
    borderBottom: '1px solid #0f3460',
  },
  success: {
    color: '#00cc00',
    fontSize: '14px',
    marginTop: '16px',
    textAlign: 'center',
  },
  error: {
    color: '#e94560',
    fontSize: '14px',
    marginTop: '16px',
  },
};

function getSprColor(spr, maxSpr = 6) {
  const denom = Math.max((maxSpr || 6) - 1, 1);
  let ratio = (spr - 1) / denom;
  ratio = Math.max(0, Math.min(1, ratio));
  const r = Math.round(20 + ratio * 140);   // dark blue -> dark red
  const g = Math.round(60 - ratio * 45);
  const b = Math.round(160 - ratio * 130);
  return [r, g, b];
}

// Tests carry their true average spots per round; the half-spot group is only used
// server-side to look benchmarks up. Show two decimals so RPH x SPR reads as HPH.
function roundSpr(val) {
  return Math.round(val * 100) / 100;
}

async function fetchDealerHistory(token, dealerId, dateFrom, dateTo, gameTypeIds = null) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (Array.isArray(gameTypeIds) && gameTypeIds.length > 0) {
    params.append('game_type_ids', gameTypeIds.join(','));
  }
  const res = await fetch(`${API}/admin/dealers/${dealerId}/history?${params}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await res.json();
  return { history: data.history || [], demPools: data.dem_pools || {} };
}

async function fetchDealerSurvey(token, dealerId, dateFrom, dateTo, gameTypeIds = null) {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (Array.isArray(gameTypeIds) && gameTypeIds.length > 0) {
    params.append('game_type_ids', gameTypeIds.join(','));
  }
  try {
    const res = await fetch(`${API}/admin/dealers/${dealerId}/survey-history?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    return Array.isArray(data.games) ? data.games : [];
  } catch (e) {
    return [];
  }
}

let _logoCache = undefined;
export function loadLogo() {
  if (_logoCache !== undefined) return Promise.resolve(_logoCache);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        _logoCache = {
          dataUrl: canvas.toDataURL('image/png'),
          aspect: img.naturalWidth / img.naturalHeight,
        };
      } catch {
        _logoCache = null;
      }
      resolve(_logoCache);
    };
    img.onerror = () => {
      _logoCache = null;
      resolve(null);
    };
    img.src = '/logo.png';
  });
}

// The benchmark a set of tests was measured against, for the header band. Tests
// keep the benchmark in force when they were dealt, so a report covering a change
// of benchmark says so rather than naming one of them.
function benchmarkLabelFor(history) {
  const labels = [...new Set((history || []).map(s => s.benchmark_label).filter(Boolean))];
  if (labels.length === 0) return null;
  if (labels.length === 1) return labels[0];
  return `${labels.length} benchmarks (see below)`;
}

function drawReportHeader(doc, { dealer, tenantName, logo, sectionLabel = null, pageText = null,
                                 benchmarkLabel = null }) {
  const pageW = doc.internal.pageSize.getWidth();
  // Logo 75%, name 50%, other text 75% of previous sizes; band shrunk to match
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageW, 48, 'F');
  doc.setDrawColor(60, 60, 60);
  doc.setLineWidth(0.6);
  doc.rect(4, 4, pageW - 8, 40, 'S');
  doc.setTextColor(0, 0, 0);
  if (logo && logo.dataUrl) {
    const logoH = 13.5;
    const logoW = logoH * logo.aspect;
    doc.addImage(logo.dataUrl, 'PNG', 10, 17, logoW, logoH);
  } else {
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('EZ PACE', 14, 24);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Time Motion System', 14, 34);
  }
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`${dealer.first_name} ${dealer.last_name}`, pageW / 2, 22, { align: 'center' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Payroll: ${dealer.payroll_number}   Shift: ${dealer.shift || 'Day'}`, pageW / 2, 32, { align: 'center' });
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(tenantName, pageW - 14, 18, { align: 'right' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date().toLocaleDateString(), pageW - 14, 28, { align: 'right' });
  if (pageText) {
    doc.text(pageText, pageW - 14, 38, { align: 'right' });
  }
  if (sectionLabel) {
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(sectionLabel, pageW / 2, 41.5, { align: 'center' });
  }
  if (benchmarkLabel) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(70, 70, 70);
    doc.text(`Benchmark: ${benchmarkLabel}`, 10, 41, { align: 'left' });
    doc.setTextColor(0, 0, 0);
  }
}

function drawNotesBlock(doc, afterY = 0, onNewPage = null) {
  // Notes block sits at the bottom of the page. If the content above reaches into
  // it, use 2 note lines instead of 4; if there's still no room, put notes and
  // signatures on their own page.
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  let notesY = pageH - 60;
  let noteLines = 4;
  if (afterY + 8 > notesY) {
    notesY = pageH - 44;
    noteLines = 2;
  }
  if (afterY + 8 > notesY) {
    doc.addPage();
    if (onNewPage) onNewPage();
    notesY = pageH - 60;
    noteLines = 4;
  }
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('Notes:', 14, notesY);
  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.3);
  for (let i = 1; i <= noteLines; i++) {
    doc.line(14, notesY + 8 * i, pageW - 14, notesY + 8 * i);
  }

  const sigY = pageH - 20;
  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.5);
  doc.setFont('helvetica', 'normal');
  doc.line(14, sigY, pageW / 2 - 20, sigY);
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text('Employee Signature', 14, sigY + 8);
  doc.line(pageW / 2 - 14, sigY, pageW / 2 - 2, sigY);
  doc.text('Date', pageW / 2 - 14, sigY + 8);
  doc.line(pageW / 2 + 8, sigY, pageW - 30, sigY);
  doc.text('Manager Signature', pageW / 2 + 8, sigY + 8);
  doc.line(pageW - 24, sigY, pageW - 14, sigY);
  doc.text('Date', pageW - 24, sigY + 8);
}

// Final section: the dealer's custom survey scores, one table per game
// (date down the left, Survey 1..5 across the top, average at the bottom).
function drawSurveySection(doc, dealer, tenantName, logo, games) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const header = () => drawReportHeader(doc, { dealer, tenantName, logo, sectionLabel: 'Dealer Survey History' });
  header();
  let y = 58;
  games.forEach((g, gi) => {
    if (gi > 0 && y > pageH - 90) {
      doc.addPage();
      header();
      y = 58;
    }
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(g.game_name, 14, y);
    y += 6;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    g.questions.forEach(q => {
      const lines = doc.splitTextToSize(`Survey ${q.slot}: ${q.question_text}  (scale 1 to ${q.scale_max})`, pageW - 28);
      doc.text(lines, 14, y);
      y += lines.length * 3.8;
    });
    y += 2;
    const head = [['Date', ...g.questions.map(q => `Survey ${q.slot}`)]];
    const body = g.rows.map(r => [
      new Date(r.date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }),
      ...g.questions.map(q => (r.scores[q.id] != null ? String(r.scores[q.id]) : '—')),
    ]);
    body.push(['AVG', ...g.questions.map(q => (g.averages[q.id] != null ? g.averages[q.id].toFixed(1) : '—'))]);
    autoTable(doc, {
      startY: y,
      head,
      body,
      theme: 'grid',
      margin: { left: 14, right: 14, top: 56 },
      styles: { cellPadding: 1.6, halign: 'center', lineColor: [110, 110, 110], lineWidth: 0.15 },
      tableLineColor: [110, 110, 110],
      tableLineWidth: 0.3,
      headStyles: { fillColor: [180, 210, 235], textColor: [0, 0, 0], fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { fillColor: [220, 235, 250], textColor: [0, 0, 0], fontSize: 9 },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      columnStyles: { 0: { halign: 'left' } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [180, 210, 235];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [233, 69, 96];
        }
      },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) header();
      },
    });
    y = doc.lastAutoTable.finalY + 10;
  });
  drawNotesBlock(doc, y - 6, header);
}

export function generateMultiSectionDealerPDF(dealer, sections, tenantName, logo = null, survey = []) {
  // sections: [{ label: string|null, history: array }, ...]
  // Each section produces its own pages within one combined PDF, with a section
  // label rendered in the header band so the reader knows which game is shown.
  // survey: Dealer Survey History games; when present it is the final section and
  // carries the notes/signatures.
  const surveyGames = (survey || []).filter(g => g.rows && g.rows.length);
  let doc = null;
  sections.forEach((section, i) => {
    const result = generateDealerPDF(dealer, section.history || [], tenantName, logo, {
      sectionLabel: section.label || null,
      demPools: section.demPools || {},
      existingDoc: doc,
      isFirstSection: i === 0,
      isLastSection: i === sections.length - 1 && surveyGames.length === 0,
    });
    if (!doc) doc = result;
  });
  if (surveyGames.length) {
    if (doc) doc.addPage();
    else doc = new jsPDF();
    drawSurveySection(doc, dealer, tenantName, logo, surveyGames);
  }
  return doc || new jsPDF();
}

export function generateDealerPDF(dealer, history, tenantName, logo = null, opts = {}) {
  const {
    sectionLabel = null,
    demPools = {},
    existingDoc = null,
    isFirstSection = true,
    isLastSection = true,
  } = opts;
  const doc = existingDoc || new jsPDF();
  if (existingDoc && !isFirstSection) {
    doc.addPage();
  }
  const pageW = doc.internal.pageSize.getWidth();

  // Pages: at most 15 tests per page, spread evenly over the fewest pages
  // (16-20 tests -> 2 pages of up to 10; 21-30 -> 2 of up to 15; 31-45 -> 3; ...).
  const MAX_PER_PAGE = 15;
  const numPages = Math.max(1, Math.ceil(history.length / MAX_PER_PAGE));
  const pageRanges = [];
  {
    const base = Math.floor(history.length / numPages);
    const extra = history.length % numPages;
    let start = 0;
    for (let p = 0; p < numPages; p++) {
      const size = base + (p < extra ? 1 : 0);
      pageRanges.push([start, start + size]);
      start += size;
    }
  }

  // DEM over exactly these tests (each test's time scaled by its own game's pool),
  // and where it ranks among that game's dealers. Percentile only for one game.
  const demRows = history.filter(s => s.dem_bench && s.dem_actual_scaled);
  const demBench = demRows.reduce((a, s) => a + s.dem_bench, 0);
  const demActual = demRows.reduce((a, s) => a + s.dem_actual_scaled, 0);
  const demRounds = demRows.reduce((a, s) => a + (s.rounds || 0), 0);
  const gameIds = Array.from(new Set(history.map(s => s.game_type_id)));
  const minDemRounds = (demPools[gameIds[0]] || {}).min_rounds || 5;
  const reportDem = demActual > 0 && demRounds >= minDemRounds ? demBench / demActual : null;
  let reportPct = null;
  if (reportDem != null && gameIds.length === 1 && (demPools[gameIds[0]]?.dems || []).length) {
    const dems = demPools[gameIds[0]].dems;
    reportPct = Math.round((100 * dems.filter(d => d <= reportDem + 1e-9).length) / dems.length);
  }
  const ordinal = (n) => {
    const v = n % 100;
    const suf = (v >= 11 && v <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
    return `${n}${suf}`;
  };

  // Overall stats — used in primary stats on page 1 and the AVG summary row on the last page
  const avgOf = (key, fixed = 1) => {
    const vals = history.filter(s => s[key]).map(s => s[key]);
    return vals.length ? parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(fixed)) : null;
  };
  const avgHph = avgOf('hph');
  const avgRph = avgOf('rph');
  const validSpr = history.filter(s => s.spr);
  const avgSpr = validSpr.length ? roundSpr(validSpr.reduce((a, b) => a + b.spr, 0) / validSpr.length) : null;
  const avgBenchRph = avgOf('benchmark_rph');
  const avgDpt = avgOf('DPT', 2);
  const avgTst = avgOf('TST', 1);
  const avgTbr = avgOf('TBR', 2);
  const avgBenchDpt = avgOf('benchmark_DPT', 2);
  const avgBenchTst = avgOf('benchmark_TST', 1);
  const avgBenchTbr = avgOf('benchmark_TBR', 2);
  const fmt = (v) => (v == null || v === 0) ? '—' : v;

  // Shared Y range across all charts so multi-page charts have comparable scales
  const rphAll = history.filter(s => s.rph).map(s => s.rph);
  const benchRphAll = history.filter(s => s.benchmark_rph).map(s => s.benchmark_rph);
  const yValues = [...rphAll, ...benchRphAll];
  const minY = yValues.length ? Math.min(...yValues) * 0.85 : 0;
  const maxY = yValues.length ? Math.max(...yValues) * 1.15 : 100;
  // SPR color scale tops out at the highest spot count seen (min 6) — Roulette -> 9.
  const chartMaxSpr = Math.max(6, ...history.map(s => Math.ceil(s.spr || 0)));

  const headerBenchmark = benchmarkLabelFor(history);

  function drawHeader(pageIdx) {
    drawReportHeader(doc, {
      dealer, tenantName, logo, sectionLabel,
      benchmarkLabel: headerBenchmark,
      pageText: numPages > 1 && pageIdx >= 0 ? `Page ${pageIdx + 1} of ${numPages}` : null,
    });
  }

  function drawPrimaryStats() {
    // 4 columns: combined "RPH @ SPR", Shuffle, TBR, DPT.
    // RPH: green if >= bench, red if <. SPR always black. Shuffle/TBR/DPT:
    // green if <= bench (lower is better), red if >. Black when no benchmark.
    doc.setFillColor(220, 235, 250);
    doc.roundedRect(14, 52, pageW - 28, 16, 2, 2, 'F');
    doc.setDrawColor(100, 150, 200);
    doc.setLineWidth(0.4);
    doc.roundedRect(14, 52, pageW - 28, 16, 2, 2, 'S');
    const inner = pageW - 28;
    const cx = (frac) => 14 + inner * frac;
    const GREEN = [0, 140, 0];
    const RED = [200, 30, 30];
    const BLACK = [0, 0, 0];
    const colorFor = (val, bench, higherIsBetter) => {
      if (val == null || bench == null || bench === 0) return BLACK;
      if (higherIsBetter) return val >= bench ? GREEN : RED;
      return val <= bench ? GREEN : RED;
    };

    // Columns 1-2: DEM (green at/above the standard 1.00, red below) and percentile.
    [
      { x: cx(1/12), text: reportDem != null ? reportDem.toFixed(2) : '—', label: 'DEM',
        color: reportDem == null ? BLACK : (reportDem >= 1 ? GREEN : RED), sub: '1.00 = standard' },
      { x: cx(3/12), text: reportPct != null ? ordinal(reportPct) : '—', label: 'Percentile',
        color: BLACK, sub: gameIds.length > 1 ? 'single game only' : 'vs dealers of this game' },
    ].forEach(c => {
      doc.setTextColor(...c.color);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(c.text, c.x, 58, { align: 'center' });
      doc.setFontSize(6);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text(c.label, c.x, 62, { align: 'center' });
      doc.text(c.sub, c.x, 66, { align: 'center' });
    });

    // Column 3: combined RPH @ SPR — RPH coloured vs benchmark, "@" and SPR always black
    const x1 = cx(5/12);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    const rphStr = String(fmt(avgRph));
    const sprStr = String(fmt(avgSpr));
    const atStr = ' @ ';
    const wRph = doc.getTextWidth(rphStr);
    const wAt = doc.getTextWidth(atStr);
    const wSpr = doc.getTextWidth(sprStr);
    const startX = x1 - (wRph + wAt + wSpr) / 2;
    const [rR, rG, rB] = colorFor(avgRph, avgBenchRph, true);
    doc.setTextColor(rR, rG, rB);
    doc.text(rphStr, startX, 58);
    doc.setTextColor(0, 0, 0);
    doc.text(atStr, startX + wRph, 58);
    doc.text(sprStr, startX + wRph + wAt, 58);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Avg RPH @ SPR', x1, 62, { align: 'center' });
    if (avgBenchRph != null && avgBenchRph !== 0) {
      doc.text(`B: ${avgBenchRph}`, x1, 66, { align: 'center' });
    }

    // Columns 2-4: Shuffle, TBR, DPT
    const cols = [
      { x: cx(7/12),  value: avgTst, label: 'Avg Shuffle', bench: avgBenchTst, higher: false },
      { x: cx(9/12),  value: avgTbr, label: 'Avg TBR',     bench: avgBenchTbr, higher: false },
      { x: cx(11/12), value: avgDpt, label: 'Avg DPT',     bench: avgBenchDpt, higher: false },
    ];
    cols.forEach(c => {
      const [r, g, b] = colorFor(c.value, c.bench, c.higher);
      doc.setTextColor(r, g, b);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(String(fmt(c.value)), c.x, 58, { align: 'center' });
      doc.setFontSize(6);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text(c.label, c.x, 62, { align: 'center' });
      if (c.bench != null && c.bench !== 0) {
        doc.text(`B: ${c.bench}`, c.x, 66, { align: 'center' });
      }
    });
  }

  // Returns the Y position where the data table should start
  function drawChart(pageHistory, chartTopY, label) {
    const chartX = 20;
    const chartY = chartTopY;
    const chartW = pageW - 40;
    const chartH = 55;

    // Light grey chart background
    doc.setFillColor(238, 240, 242);
    doc.rect(chartX, chartY, chartW, chartH, 'F');
    doc.setDrawColor(170, 174, 180);
    doc.setLineWidth(0.4);
    doc.rect(chartX, chartY, chartW, chartH, 'S');

    doc.setDrawColor(200, 203, 208);
    doc.setLineWidth(0.3);
    const yTicks = 5;
    for (let i = 0; i <= yTicks; i++) {
      const val = Math.round(minY + (i / yTicks) * (maxY - minY));
      const y = chartY + chartH - (i / yTicks) * chartH;
      doc.line(chartX, y, chartX + chartW, y);
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(String(val), chartX - 2, y + 1.2, { align: 'right' });
    }

    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    doc.text('RPH', chartX - 2, chartY - 2, { align: 'right' });
    if (label) {
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(label, chartX, chartY - 3);
      doc.setFont('helvetica', 'normal');
    }

    const xMargin = 8;
    const xPos = (idx) =>
      chartX + xMargin + (idx / Math.max(pageHistory.length - 1, 1)) * (chartW - xMargin * 2);

    pageHistory.forEach((session, idx) => {
      if (idx % Math.max(1, Math.floor(pageHistory.length / 7)) === 0) {
        doc.setFontSize(7.5);
        doc.setTextColor(0, 0, 0);
        doc.text(
          new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          xPos(idx), chartY + chartH + 6, { align: 'center' }
        );
      }
    });

    pageHistory.forEach((session, idx) => {
      if (!session.rph) return;
      const x = xPos(idx);
      const y = chartY + chartH - ((session.rph - minY) / (maxY - minY)) * chartH;
      const [r, g, b] = session.spr ? getSprColor(session.spr, chartMaxSpr) : [120, 120, 120];
      doc.setFillColor(r, g, b);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.5);
      doc.circle(x, y, 2.5, 'FD');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(String(session.rph), x, y - 4.2, { align: 'center' });
      doc.setFont('helvetica', 'normal');
    });

    // Benchmark markers drawn after the dots (on top); bright green above, bright yellow under.
    doc.setLineWidth(0.8);
    pageHistory.forEach((session, idx) => {
      if (!session.benchmark_rph) return;
      const x = xPos(idx);
      const y = chartY + chartH - ((session.benchmark_rph - minY) / (maxY - minY)) * chartH;
      const above = session.rph != null && session.rph >= session.benchmark_rph;
      if (above) doc.setDrawColor(0, 200, 0); else doc.setDrawColor(255, 205, 0);
      doc.line(x - 1.8, y, x + 1.8, y);
      doc.line(x, y - 1.8, x, y + 1.8);
    });

    // Legend
    const legendY = chartY + chartH + 14;
    doc.setDrawColor(0, 200, 0);
    doc.setLineWidth(0.6);
    doc.line(chartX - 0.8, legendY, chartX + 2.8, legendY);
    doc.line(chartX + 1, legendY - 1.8, chartX + 1, legendY + 1.8);
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text('Benchmark (green above / yellow under)', chartX + 5, legendY + 1);

    const sprValues = Array.from({ length: chartMaxSpr }, (_, i) => i + 1);
    doc.setFontSize(8);
    doc.text('Spots PH:', chartX + 96, legendY + 1);
    const dotStartX = chartX + 116;
    const dotSpan = chartW - 116;
    sprValues.forEach((spr, i) => {
      const cx = dotStartX + (dotSpan / Math.max(sprValues.length - 1, 1)) * i;
      const [r, g, b] = getSprColor(spr, chartMaxSpr);
      doc.setFillColor(r, g, b);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.3);
      doc.circle(cx, legendY, 1.8, 'FD');
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(String(spr), cx + 3, legendY + 1);
    });

    return legendY + 10;
  }

  // Every process code present in this game's tests, in the standard order.
  const presentCodes = new Set();
  history.forEach(h => Object.keys(h.codes || {}).forEach(c => presentCodes.add(c)));
  const codeList = [
    ...CODE_ORDER.filter(c => presentCodes.has(c)),
    ...[...presentCodes].filter(c => !CODE_ORDER.includes(c)).sort(),
  ];
  const one = (v) => (v == null ? null : Number(v).toFixed(1));
  // "dealer / benchmark" in one cell to save width.
  const pair = (v, b) => (v == null ? '—' : `${one(v)} / ${b == null || b === 0 ? '—' : one(b)}`);
  const codeAvg = (code, key) => {
    const vals = history.map(h => h.codes?.[code]?.[key]).filter(v => v != null);
    return vals.length ? vals.reduce((x, y) => x + y, 0) / vals.length : null;
  };
  // TRT is DPT + TBR. Built from the one-decimal figures printed beside it, so the
  // row adds up on paper rather than drifting by a tenth from separate rounding.
  const withTrt = (get) => (c, key) => {
    if (c !== 'TRT') return get(c, key);
    const d = get('DPT', key), t = get('TBR', key);
    if (d == null || t == null) return get(c, key);
    return Math.round(d * 10) / 10 + Math.round(t * 10) / 10;
  };
  const avgBenchHph = avgOf('benchmark_hph');

  function drawDataTable(startY, pageHistory, includeAverageRow) {
    const tableHead = [['Date', 'Rds', 'RPH', 'SPR', 'HPH', ...codeList]];
    const tableBody = pageHistory.map(s => [
      new Date(s.date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }),
      fmt(s.rounds),
      pair(s.rph, s.benchmark_rph),
      fmt(s.spr),
      pair(s.hph, s.benchmark_hph),
      ...codeList.map(c => {
        const get = withTrt((code, key) => s.codes?.[code]?.[key]);
        return pair(get(c, 'avg'), get(c, 'benchmark'));
      }),
    ]);
    if (includeAverageRow) {
      tableBody.push([
        'AVG', '—',
        pair(avgRph, avgBenchRph),
        fmt(avgSpr),
        pair(avgHph, avgBenchHph),
        ...codeList.map(c => pair(withTrt(codeAvg)(c, 'avg'), withTrt(codeAvg)(c, 'benchmark'))),
      ]);
    }
    // Shrink the font a little when a game has many codes so the row stays on one line.
    const nCols = 5 + codeList.length;
    const fontSize = nCols <= 11 ? 7.5 : nCols <= 13 ? 6.8 : 6.2;
    doc.setFontSize(7);
    doc.setTextColor(60, 60, 60);
    doc.text('Each cell: dealer / benchmark. Benchmarks are matched to the table conditions of each round.',
      14, startY - 1.5);
    autoTable(doc, {
      startY,
      head: tableHead,
      body: tableBody,
      theme: 'grid',
      margin: { left: 14, right: 14 },
      styles: { cellPadding: 1.1, overflow: 'linebreak', halign: 'center', lineColor: [110, 110, 110], lineWidth: 0.15 },
      tableLineColor: [110, 110, 110],
      tableLineWidth: 0.3,
      headStyles: { fillColor: [180, 210, 235], textColor: [0, 0, 0], fontSize, fontStyle: 'bold' },
      bodyStyles: { fillColor: [220, 235, 250], textColor: [0, 0, 0], fontSize },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      didParseCell: (data) => {
        if (includeAverageRow && data.section === 'body' && data.row.index === tableBody.length - 1) {
          data.cell.styles.fillColor = [180, 210, 235];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [233, 69, 96];
        }
      },
    });
  }

  function drawNotesAndSignatures(afterY = 0) {
    drawNotesBlock(doc, afterY, () => drawHeader(-1));
  }

  if (history.length === 0) {
    drawHeader(0);
    doc.setTextColor(160, 160, 176);
    doc.setFontSize(12);
    doc.text(
      sectionLabel
        ? `No ${sectionLabel} sessions for this period.`
        : 'No session data found for this period.',
      14,
      sectionLabel ? 96 : 90
    );
    if (isLastSection) {
      drawNotesAndSignatures();
    }
    return doc;
  }

  for (let pageIdx = 0; pageIdx < numPages; pageIdx++) {
    if (pageIdx > 0) doc.addPage();
    const [start, end] = pageRanges[pageIdx];
    const pageHistory = history.slice(start, end);
    const isFirstPage = pageIdx === 0;
    const isLastPage = pageIdx === numPages - 1;

    drawHeader(pageIdx);

    let chartTopY;
    if (isFirstPage) {
      drawPrimaryStats();
      chartTopY = sectionLabel ? 80 : 76;
    } else {
      chartTopY = 56;
    }

    const chartLabel = numPages > 1 ? `Sessions ${start + 1}–${end}` : null;
    const tableStartY = drawChart(pageHistory, chartTopY, chartLabel);

    drawDataTable(tableStartY, pageHistory, isLastPage);

    if (isLastPage && isLastSection) {
      drawNotesAndSignatures(doc.lastAutoTable ? doc.lastAutoTable.finalY : 0);
    }
  }

  return doc;
}

function generateSummaryPDF(dealers, sortedData, tenantName, dateFrom, dateTo, shift, logo = null) {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();

  // Header — same pattern as dealer report: white bg, dark border, logo on left
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageW, 48, 'F');
  doc.setDrawColor(60, 60, 60);
  doc.setLineWidth(0.6);
  doc.rect(4, 4, pageW - 8, 40, 'S');
  doc.setTextColor(0, 0, 0);
  if (logo && logo.dataUrl) {
    const logoH = 13.5;
    const logoW = logoH * logo.aspect;
    doc.addImage(logo.dataUrl, 'PNG', 10, 17, logoW, logoH);
  } else {
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('EZ PACE', 14, 24);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Time Motion System', 14, 34);
  }
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Department Summary', pageW / 2, 22, { align: 'center' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const period = dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : 'All time';
  doc.text(`Shift: ${shift || 'All'}  ·  ${period}  ·  ${sortedData.length} dealers`, pageW / 2, 32, { align: 'center' });
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(tenantName, pageW - 14, 18, { align: 'right' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date().toLocaleDateString(), pageW - 14, 28, { align: 'right' });

  // Light blue stat band — overall averages across all dealers
  const totals = sortedData.reduce((acc, r) => {
    if (r.avgRph) { acc.rphSum += r.avgRph; acc.rphN++; }
    if (r.avgSpr) { acc.sprSum += r.avgSpr; acc.sprN++; }
    if (r.avgHph) { acc.hphSum += r.avgHph; acc.hphN++; }
    acc.sessions += (r.history?.length || 0);
    return acc;
  }, { rphSum: 0, rphN: 0, sprSum: 0, sprN: 0, hphSum: 0, hphN: 0, sessions: 0 });
  const overallRph = totals.rphN ? (totals.rphSum / totals.rphN).toFixed(1) : '—';
  const overallSpr = totals.sprN ? (totals.sprSum / totals.sprN).toFixed(2) : '—';
  const overallHph = totals.hphN ? (totals.hphSum / totals.hphN).toFixed(1) : '—';

  doc.setFillColor(220, 235, 250);
  doc.roundedRect(14, 52, pageW - 28, 16, 2, 2, 'F');
  doc.setDrawColor(100, 150, 200);
  doc.setLineWidth(0.4);
  doc.roundedRect(14, 52, pageW - 28, 16, 2, 2, 'S');
  const inner = pageW - 28;
  const cx = (frac) => 14 + inner * frac;
  const statCols = [
    { x: cx(1/8), value: `${overallRph} @ ${overallSpr}`, label: 'Dept Avg RPH @ SPR' },
    { x: cx(3/8), value: overallHph,            label: 'Dept Avg HPH' },
    { x: cx(5/8), value: totals.sessions,       label: 'Total Sessions' },
    { x: cx(7/8), value: sortedData.length,     label: 'Dealers' },
  ];
  statCols.forEach(c => {
    doc.setTextColor(233, 69, 96);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(String(c.value), c.x, 60, { align: 'center' });
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(c.label, c.x, 65, { align: 'center' });
  });

  const tableHead = [['#', 'Payroll', 'Name', 'Shift', 'Sessions', 'Avg RPH @ SPR', 'Avg HPH']];
  const tableBody = sortedData.map((row, i) => [
    i + 1,
    row.dealer.payroll_number,
    `${row.dealer.first_name} ${row.dealer.last_name}`,
    row.dealer.shift || 'Day',
    row.history.length,
    row.avgRph && row.avgSpr ? `${row.avgRph} @ ${row.avgSpr}` : '—',
    row.avgHph || '—',
  ]);

  autoTable(doc, {
    startY: 76,
    head: tableHead,
    body: tableBody,
    theme: 'grid',
    headStyles: { fillColor: [180, 210, 235], textColor: [0, 0, 0], fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fillColor: [220, 235, 250], textColor: [0, 0, 0], fontSize: 9 },
    alternateRowStyles: { fillColor: [240, 240, 240] },
  });

  return doc;
}

function generateSummaryExcel(sortedData, tenantName, dateFrom, dateTo, shift) {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Summary (one row per dealer)
  const period = dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : 'All time';
  const summaryRows = [
    ['EZ Pace — Department Summary'],
    [`Tenant: ${tenantName}`],
    [`Shift: ${shift || 'All'}    Period: ${period}    Dealers: ${sortedData.length}`],
    [`Generated: ${new Date().toLocaleString()}`],
    [],
    ['#', 'Payroll', 'Name', 'Shift', 'Sessions', 'Avg RPH', 'Avg SPR', 'Avg HPH'],
    ...sortedData.map((row, i) => [
      i + 1,
      row.dealer.payroll_number,
      `${row.dealer.first_name} ${row.dealer.last_name}`,
      row.dealer.shift || 'Day',
      row.history.length,
      row.avgRph || '',
      row.avgSpr || '',
      row.avgHph || '',
    ]),
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(summaryRows);
  ws1['!cols'] = [{ wch: 4 }, { wch: 12 }, { wch: 24 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

  // Sheet 2: Sessions (one row per session across all dealers)
  const sessionHeader = ['Dealer Payroll', 'Dealer Name', 'Shift', 'Date', 'Rounds', 'RPH', 'SPR', 'HPH', 'RPH Bench', 'DPT', 'DPT Bench', 'TRT', 'TRT Bench', 'TST', 'TST Bench', 'TBR', 'TBR Bench'];
  const sessionRows = [sessionHeader];
  sortedData.forEach(row => {
    (row.history || []).forEach(s => {
      sessionRows.push([
        row.dealer.payroll_number,
        `${row.dealer.first_name} ${row.dealer.last_name}`,
        row.dealer.shift || 'Day',
        s.date ? s.date.split('T')[0] : '',
        s.rounds || '',
        s.rph || '',
        s.spr || '',
        s.hph || '',
        s.benchmark_rph || '',
        s.DPT || '',
        s.benchmark_DPT || '',
        s.TRT || '',
        s.benchmark_TRT || '',
        s.TST || '',
        s.benchmark_TST || '',
        s.TBR || '',
        s.benchmark_TBR || '',
      ]);
    });
  });
  const ws2 = XLSX.utils.aoa_to_sheet(sessionRows);
  ws2['!cols'] = sessionHeader.map(() => ({ wch: 12 }));
  XLSX.utils.book_append_sheet(wb, ws2, 'Sessions');

  return wb;
}

function BatchReportsTab() {
  const token = localStorage.getItem('admin_token');
  const [dealers, setDealers] = useState([]);
  const [shift, setShift] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('alpha');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [preview, setPreview] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [tenantName, setTenantName] = useState('Casino');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dealerSearch, setDealerSearch] = useState('');
  const [gameTypes, setGameTypes] = useState([]);
  const [selectedItemKeys, setSelectedItemKeys] = useState(new Set());

  useEffect(() => {
    loadDealers();
    loadGameTypes();
    const name = localStorage.getItem('admin_name');
    if (name) setTenantName(name);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadDealers() {
    const res = await fetch(`${API}/admin/dealers`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (Array.isArray(data)) setDealers(data.filter(d => d.active));
  }

  async function loadGameTypes() {
    const res = await fetch(`${API}/game-types`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (Array.isArray(data)) setGameTypes(data);
  }

  // Group game types by family. Families with 2+ members get a rollup item;
  // singleton "families" (unfamilied games) become a single item.
  const families = (() => {
    const map = new Map();
    gameTypes.forEach(g => {
      const key = g.family_id || `__solo__${g.id}`;
      const name = g.family_id ? g.family_name : g.name;
      if (!map.has(key)) map.set(key, { id: key, name, family_id: g.family_id, games: [] });
      map.get(key).games.push(g);
    });
    return Array.from(map.values());
  })();
  const pickerItems = (() => {
    const items = [];
    families.forEach(family => {
      if (family.family_id && family.games.length >= 2) {
        items.push({
          key: `f:${family.id}`,
          label: family.name,
          kind: 'family',
          gameIds: family.games.map(g => String(g.id)),
        });
      }
      family.games.forEach(g => {
        items.push({
          key: `g:${g.id}`,
          label: g.name,
          kind: 'game',
          gameIds: [String(g.id)],
        });
      });
    });
    return items;
  })();
  function togglePickerItem(key) {
    setSelectedItemKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const visibleDealers = dealers.filter(d => {
    if (shift && d.shift !== shift) return false;
    if (dealerSearch) {
      const q = dealerSearch.toLowerCase();
      const name = `${d.first_name} ${d.last_name}`.toLowerCase();
      const payroll = (d.payroll_number || '').toLowerCase();
      if (!name.includes(q) && !payroll.includes(q)) return false;
    }
    return true;
  });

  const selectedDealers = dealers.filter(d => selectedIds.has(d.id));

  function toggleDealer(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAllVisible() {
    setSelectedIds(prev => {
      const next = new Set(prev);
      visibleDealers.forEach(d => next.add(d.id));
      return next;
    });
  }

  function clearAll() {
    setSelectedIds(new Set());
  }

  async function generateReports() {
    if (selectedDealers.length === 0) {
      setError('Select at least one dealer');
      return;
    }
    if (selectedItemKeys.size === 0) {
      setError('Select at least one game (or family rollup) to include');
      return;
    }
    setLoading(true);
    setProgress(0);
    setMessage('');
    setError('');

    const logo = await loadLogo();
    const zip = new JSZip();
    const allData = [];

    const sectionItems = pickerItems.filter(it => selectedItemKeys.has(it.key));
    const unionGameIds = Array.from(new Set(sectionItems.flatMap(it => it.gameIds)));

    for (let i = 0; i < selectedDealers.length; i++) {
      const dealer = selectedDealers[i];
      setProgressText(`Generating report for ${dealer.first_name} ${dealer.last_name} (${i + 1}/${selectedDealers.length})`);
      setProgress(Math.round((i / selectedDealers.length) * 90));

      // Fetch each section's history via the backend game_type_ids filter (reliable),
      // plus one fetch for the summary roll-up across the union of selected items.
      const sections = [];
      for (const item of sectionItems) {
        const { history: sectionHistory, demPools } = await fetchDealerHistory(token, dealer.id, dateFrom, dateTo, item.gameIds);
        sections.push({ label: item.label, history: sectionHistory, demPools });
      }
      const { history: summaryHistory } = await fetchDealerHistory(token, dealer.id, dateFrom, dateTo, unionGameIds);
      const survey = await fetchDealerSurvey(token, dealer.id, dateFrom, dateTo, unionGameIds);
      const validHph = summaryHistory.filter(s => s.hph);
      const validRph = summaryHistory.filter(s => s.rph);
      const validSpr = summaryHistory.filter(s => s.spr);
      const avgHph = validHph.length ? parseFloat((validHph.reduce((a, b) => a + b.hph, 0) / validHph.length).toFixed(1)) : null;
      const avgRph = validRph.length ? parseFloat((validRph.reduce((a, b) => a + b.rph, 0) / validRph.length).toFixed(1)) : null;
      const avgSpr = validSpr.length ? roundSpr(validSpr.reduce((a, b) => a + b.spr, 0) / validSpr.length) : null;

      allData.push({ dealer, history: summaryHistory, avgHph, avgRph, avgSpr });

      const pdf = generateMultiSectionDealerPDF(dealer, sections, tenantName, logo, survey);
      const pdfBlob = pdf.output('arraybuffer');
      const filename = `${dealer.payroll_number}_${dealer.last_name}_${dealer.first_name}.pdf`;
      zip.file(filename, pdfBlob);
    }

    let sorted = [...allData];
    if (sortBy === 'hph_desc') sorted.sort((a, b) => (b.avgHph || 0) - (a.avgHph || 0));
    else if (sortBy === 'hph_asc') sorted.sort((a, b) => (a.avgHph || 0) - (b.avgHph || 0));
    else sorted.sort((a, b) => a.dealer.last_name.localeCompare(b.dealer.last_name));

    setProgressText('Generating summary report...');
    setProgress(92);
    const summaryPdf = generateSummaryPDF(dealers, sorted, tenantName, dateFrom, dateTo, shift, logo);
    zip.file('_SUMMARY_REPORT.pdf', summaryPdf.output('arraybuffer'));

    setProgressText('Creating ZIP file...');
    setProgress(96);
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ezpace_reports_${new Date().toISOString().split('T')[0]}.zip`;
    a.click();
    URL.revokeObjectURL(url);

    setProgress(100);
    setProgressText('');
    setPreview(sorted);
    setMessage(`Successfully generated ${selectedDealers.length} dealer reports + 1 summary report`);
    setLoading(false);
  }

  async function downloadExcel() {
    if (selectedDealers.length === 0) {
      setError('Select at least one dealer');
      return;
    }
    setLoading(true);
    setProgress(0);
    setMessage('');
    setError('');

    const allData = [];
    for (let i = 0; i < selectedDealers.length; i++) {
      const dealer = selectedDealers[i];
      setProgressText(`Loading data for ${dealer.first_name} ${dealer.last_name} (${i + 1}/${selectedDealers.length})`);
      setProgress(Math.round((i / selectedDealers.length) * 90));
      const history = await fetchDealerHistory(token, dealer.id, dateFrom, dateTo);
      const validHph = history.filter(s => s.hph);
      const validRph = history.filter(s => s.rph);
      const validSpr = history.filter(s => s.spr);
      const avgHph = validHph.length ? parseFloat((validHph.reduce((a, b) => a + b.hph, 0) / validHph.length).toFixed(1)) : null;
      const avgRph = validRph.length ? parseFloat((validRph.reduce((a, b) => a + b.rph, 0) / validRph.length).toFixed(1)) : null;
      const avgSpr = validSpr.length ? roundSpr(validSpr.reduce((a, b) => a + b.spr, 0) / validSpr.length) : null;
      allData.push({ dealer, history, avgHph, avgRph, avgSpr });
    }

    let sorted = [...allData];
    if (sortBy === 'hph_desc') sorted.sort((a, b) => (b.avgHph || 0) - (a.avgHph || 0));
    else if (sortBy === 'hph_asc') sorted.sort((a, b) => (a.avgHph || 0) - (b.avgHph || 0));
    else sorted.sort((a, b) => a.dealer.last_name.localeCompare(b.dealer.last_name));

    setProgressText('Building Excel file...');
    setProgress(95);
    const wb = generateSummaryExcel(sorted, tenantName, dateFrom, dateTo, shift);
    XLSX.writeFile(wb, `ezpace_summary_${new Date().toISOString().split('T')[0]}.xlsx`);

    setProgress(100);
    setProgressText('');
    setPreview(sorted);
    setMessage(`Excel file downloaded with summary + ${sorted.reduce((n, r) => n + (r.history?.length || 0), 0)} session rows`);
    setLoading(false);
  }

  return (
    <>
      {error && <div style={styles.error}>{error}</div>}

        <div style={styles.card}>
          <div style={styles.cardTitle}>Games to Include</div>
          <div style={{ color: '#a0a0b0', fontSize: '13px', marginBottom: '16px' }}>
            Each item produces its own section in every dealer's PDF. Family rollups (e.g.,
            "All Blackjack") pool the family's games into a single section. Pick one or more.
          </div>
          {pickerItems.length === 0 ? (
            <div style={{ color: '#a0a0b0', fontSize: '13px' }}>
              No games installed. Visit Settings to install Blackjack EZ.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {pickerItems.map(item => {
                const selected = selectedItemKeys.has(item.key);
                return (
                  <button
                    key={item.key}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#0f3460',
                      border: selected ? '2px solid #e94560' : '2px solid #0f3460',
                      borderRadius: '8px',
                      color: selected ? '#ffffff' : '#a0a0b0',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                    }}
                    onClick={() => togglePickerItem(item.key)}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Report Parameters</div>
          <div style={styles.filterGrid}>
            <div style={styles.filterGroup}>
              <label style={styles.label}>Shift</label>
              <select style={styles.select} value={shift} onChange={e => setShift(e.target.value)}>
                <option value="">All Shifts ({dealers.length} dealers)</option>
                <option value="Day">Day ({dealers.filter(d => d.shift === 'Day' || !d.shift).length} dealers)</option>
                <option value="Swing">Swing ({dealers.filter(d => d.shift === 'Swing').length} dealers)</option>
                <option value="Grave">Grave ({dealers.filter(d => d.shift === 'Grave').length} dealers)</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.label}>Date From</label>
              <input
                style={styles.input}
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
              />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.label}>Date To</label>
              <input
                style={styles.input}
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
              />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.label}>Sort Summary By</label>
              <select style={styles.select} value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="alpha">Alphabetical</option>
                <option value="hph_desc">HPH — Highest First</option>
                <option value="hph_asc">HPH — Lowest First</option>
              </select>
            </div>
          </div>
          <div style={{ color: '#a0a0b0', fontSize: '12px' }}>
            Shift narrows the roster below. Use the checkboxes to choose who to report on.
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={styles.cardTitle}>Select Dealers</div>
            <div style={{ color: '#a0a0b0', fontSize: '14px' }}>
              {selectedDealers.length} of {dealers.length} selected
              {dealerSearch || shift ? `  ·  ${visibleDealers.length} visible` : ''}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input
              style={{ ...styles.input, flex: '1 1 240px' }}
              placeholder="Search name or payroll #"
              value={dealerSearch}
              onChange={e => setDealerSearch(e.target.value)}
            />
            <button
              style={{ padding: '10px 18px', backgroundColor: '#0f3460', border: '1px solid #0f3460', borderRadius: '8px', color: '#ffffff', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' }}
              onClick={selectAllVisible}
              disabled={visibleDealers.length === 0}
            >
              Select All Visible
            </button>
            <button
              style={{ padding: '10px 18px', backgroundColor: 'transparent', border: '1px solid #0f3460', borderRadius: '8px', color: '#a0a0b0', fontSize: '13px', cursor: 'pointer' }}
              onClick={clearAll}
              disabled={selectedIds.size === 0}
            >
              Clear All
            </button>
          </div>

          <div style={{ maxHeight: '380px', overflowY: 'auto', border: '1px solid #0f3460', borderRadius: '8px' }}>
            <table style={styles.previewTable}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#16213e', zIndex: 1 }}>
                <tr>
                  <th style={{ ...styles.th, width: '40px' }}></th>
                  <th style={styles.th}>Payroll</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Shift</th>
                </tr>
              </thead>
              <tbody>
                {visibleDealers.map(d => {
                  const checked = selectedIds.has(d.id);
                  return (
                    <tr
                      key={d.id}
                      onClick={() => toggleDealer(d.id)}
                      style={{ cursor: 'pointer', backgroundColor: checked ? '#0f3460' : 'transparent' }}
                    >
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {}}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                      </td>
                      <td style={styles.td}>{d.payroll_number}</td>
                      <td style={styles.td}>{d.first_name} {d.last_name}</td>
                      <td style={styles.td}>{d.shift || 'Day'}</td>
                    </tr>
                  );
                })}
                {visibleDealers.length === 0 && (
                  <tr>
                    <td colSpan="4" style={{ ...styles.td, color: '#a0a0b0', textAlign: 'center', padding: '24px' }}>
                      No dealers match the current filter
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              style={(loading || selectedDealers.length === 0 || selectedItemKeys.size === 0) ? styles.disabledButton : styles.generateButton}
              onClick={generateReports}
              disabled={loading || selectedDealers.length === 0 || selectedItemKeys.size === 0}
            >
              {loading
                ? 'Working...'
                : selectedDealers.length === 0
                  ? 'Select at least one dealer'
                  : selectedItemKeys.size === 0
                    ? 'Select at least one game'
                    : `Generate ${selectedDealers.length} PDF${selectedDealers.length !== 1 ? 's' : ''} + Summary`}
            </button>
            <button
              style={(loading || selectedDealers.length === 0)
                ? styles.disabledButton
                : { ...styles.generateButton, backgroundColor: '#1d6f42' }}
              onClick={downloadExcel}
              disabled={loading || selectedDealers.length === 0}
              title="Download an .xlsx file with the summary table and per-session data"
            >
              {loading ? 'Working...' : 'Download Excel'}
            </button>
          </div>

          {loading && (
            <div>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${progress}%` }} />
              </div>
              <div style={styles.progressText}>{progressText}</div>
            </div>
          )}

          {message && <div style={styles.success}>{message}</div>}
        </div>

      {preview.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Report Preview</div>
          <table style={styles.previewTable}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Payroll</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Shift</th>
                <th style={styles.th}>Sessions</th>
                <th style={styles.th}>Avg RPH @ SPR</th>
                <th style={styles.th}>Avg HPH</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((row, i) => (
                <tr key={row.dealer.id}>
                  <td style={styles.td}>{i + 1}</td>
                  <td style={styles.td}>{row.dealer.payroll_number}</td>
                  <td style={styles.td}>{row.dealer.first_name} {row.dealer.last_name}</td>
                  <td style={styles.td}>{row.dealer.shift || 'Day'}</td>
                  <td style={styles.td}>{row.history.length}</td>
                  <td style={styles.td}>{row.avgRph && row.avgSpr ? `${row.avgRph} @ ${row.avgSpr}` : '—'}</td>
                  <td style={styles.td}>{row.avgHph || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default BatchReportsTab;